import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase/config';
import { 
    collection, 
    query, 
    doc, 
    orderBy, 
    onSnapshot, 
    deleteDoc 
} from 'firebase/firestore';

// IMPORTAMOS LA UTILIDAD DE BORRADO PROFUNDO
import { deletePostVentaFull } from '../../../../firebase/firebaseUtils.js';

import ModalPostVenta from '../../../ui/ModalPostVenta.jsx';
import ModalConfirmacion from '../../../ui/ModalConfirmacion.jsx';
import ModalNotificacion from '../../../ui/ModalNotificacion.jsx';
import { getPersonaChipClass, getSiniestroStatusChip } from '../../../../utils/styles';

/**
 * PostVentaTab - Gestión de seguimiento postventa para una póliza específica.
 * Sincronización en tiempo real para estado, número de gestión e historial de etapas.
 */
const PostVentaTab = ({ poliza, client }) => {
    const [gestiones, setGestiones] = useState([]);
    const [polizaData, setPolizaData] = useState(poliza);
    const [loading, setLoading] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [editingGestion, setEditingGestion] = useState(null);
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
    const [deletionConfirm, setDeletionConfirm] = useState({ show: false, itemToDelete: null });
    const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);

    const polizaId = poliza?.id;
    const clientId = client?.id;

    // --- 1. LISTENER DE LA PÓLIZA (Tiempo Real para Estado y N. Gestión) ---
    useEffect(() => {
        if (!polizaId || !clientId) return;
        const pRef = doc(db, `public/data/clientes/${clientId}/polizas`, polizaId);
        const unsub = onSnapshot(pRef, (snap) => {
            if (snap.exists()) {
                setPolizaData({ id: snap.id, ...snap.data() });
            }
        }, (error) => {
            console.error("Error en listener raíz PostVentaTab:", error);
        });
        return () => unsub();
    }, [polizaId, clientId]);

    // --- 2. LISTENER DE LAS ETAPAS (Tiempo Real para el Historial) ---
    useEffect(() => {
        if (!polizaId || !clientId) return;

        setLoading(true);
        const path = `public/data/clientes/${clientId}/polizas/${polizaId}/gestiones_postventa`;
        const collectionRef = collection(db, path);
        // Ordenamos por fechaCreacion (que ahora incluye hora) para un orden perfecto
        const q = query(collectionRef, orderBy('fechaCreacion', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(d => ({ 
                id: d.id, 
                ...d.data() 
            }));
            setGestiones(data);
            setLoading(false);
        }, (error) => {
            console.error("Error en listener historial PostVentaTab:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [polizaId, clientId]);

    const renderSmartDate = (val) => {
        if (!val) return '-';
        // Si es un Timestamp de Firebase
        if (val.seconds) return new Date(val.seconds * 1000).toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
        // Si es un string YYYY-MM-DD
        if (typeof val === 'string' && val.includes('-')) {
            const parts = val.split('T')[0].split('-');
            return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : val;
        }
        return String(val);
    };

    const handleDeleteStage = async () => {
        if (!deletionConfirm.itemToDelete) return;
        const stageId = deletionConfirm.itemToDelete.id;
        setDeletionConfirm({ show: false, itemToDelete: null });
        try {
            const pathStage = `public/data/clientes/${clientId}/polizas/${polizaId}/gestiones_postventa/${stageId}`;
            await deleteDoc(doc(db, pathStage));
            setNotification({ show: true, message: 'Etapa eliminada correctamente del legajo.', type: 'success' });
        } catch (e) {
            setNotification({ show: true, message: 'Ocurrió un error al intentar eliminar la etapa.', type: 'error' });
        }
    };

    const handleDeleteFull = async () => {
        setDeleteAllConfirm(false);
        try {
            await deletePostVentaFull(clientId, polizaId);
            setNotification({ show: true, message: 'Se ha eliminado todo el legajo de Post Venta correctamente.', type: 'success' });
        } catch (e) {
            setNotification({ show: true, message: 'Error al eliminar el legajo completo.', type: 'error' });
        }
    };

    return (
        <div className="space-y-6 animate-fade-in font-sans text-left pb-10">

            {/* HEADER - ESTILO PREMIUM INDIGO */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 flex justify-between items-center shadow-sm relative overflow-hidden shrink-0">
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-indigo-500"></div>
                
                <div className="flex items-center gap-12">
                    <div>
                        <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-1 text-left">Estado PostVenta</p>
                        <div className="flex items-center gap-3">
                            <span className={`px-4 py-1 rounded-full text-[11px] font-black uppercase tracking-wide border ${getSiniestroStatusChip(polizaData?.postventa_estado || 'Abierto')}`}>
                                {polizaData?.postventa_estado || 'Sin Iniciar'}
                            </span>
                        </div>
                    </div>
                    
                    {polizaData?.postventa_numero_gestion && (
                        <div className="border-l border-slate-100 pl-12 text-left">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">N. Gestión</p>
                            <span className="text-lg font-bold text-slate-800 tabular-nums">
                                {polizaData.postventa_numero_gestion}
                            </span>
                        </div>
                    )}
                </div>
                
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setDeleteAllConfirm(true)}
                        className="p-2.5 bg-red-50 text-red-600 rounded-xl border border-red-100 hover:bg-red-600 hover:text-white transition-all shadow-sm group"
                        title="Eliminar Post Venta completo"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>

                    <button
                        onClick={() => { setEditingGestion(null); setShowModal(true); }}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center gap-2 shadow-lg uppercase tracking-widest active:scale-95"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path d="M12 4v16m8-8H4"></path></svg>
                        Cargar Etapa
                    </button>
                </div>
            </div>

            {/* TABLA DE GESTIONES */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm overflow-x-auto custom-scrollbar">
                <table className="w-full text-left border-collapse text-[13px] min-w-[1100px]">
                    <thead className="bg-slate-900 text-white uppercase font-bold text-[9px] tracking-widest">
                        <tr>
                            <th className="p-4 w-32 whitespace-nowrap">Fecha Gestión</th>
                            <th className="p-4 w-56">Etapa de la Gestión</th>
                            <th className="p-4 w-40 text-center">Responsable</th>
                            <th className="p-4">Observaciones de la Etapa</th>
                            <th className="p-4 w-32 text-center whitespace-nowrap">Próxima Gestión</th>
                            <th className="p-4 w-24 text-right pr-8 sticky right-0 bg-slate-900 shadow-[-5px_0_10px_rgba(0,0,0,0.3)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan="6" className="p-16 text-center text-slate-400 font-bold animate-pulse uppercase tracking-widest">Sincronizando legajo...</td></tr>
                        ) : gestiones.length === 0 ? (
                            <tr><td colSpan="6" className="p-16 text-center text-slate-400 italic font-medium">No hay registros de seguimiento postventa.</td></tr>
                        ) : (
                            gestiones.map(g => (
                                <tr key={g.id} className="hover:bg-slate-50 transition-colors group">
                                    <td className="p-4 font-bold text-slate-500 tabular-nums whitespace-nowrap">
                                        {renderSmartDate(g.fechaCreacion)}
                                    </td>
                                    <td className="p-4">
                                        <span className="bg-indigo-50 border border-indigo-100 rounded-lg text-[11px] text-indigo-800 font-bold uppercase px-3 py-1">
                                            {g.tipoGestion || '-'}
                                        </span>
                                    </td>
                                    <td className="p-4 text-center whitespace-nowrap">
                                        {g.quienGestiono ? (
                                            <div className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide inline-block ${getPersonaChipClass(g.quienGestiono)}`}>
                                                {g.quienGestiono}
                                            </div>
                                        ) : '-'}
                                    </td>
                                    <td className="p-4 text-left">
                                        <div className="max-w-md">
                                            <p className="text-slate-600 font-medium leading-relaxed truncate" title={g.comentario || g.comentarios}>
                                                {g.comentario || g.comentarios || '-'}
                                            </p>
                                        </div>
                                    </td>
                                    <td className="p-4 text-center font-bold text-slate-800 tabular-nums whitespace-nowrap">
                                        {renderSmartDate(g.fechaProximaGestion)}
                                    </td>
                                    <td className="p-4 text-right pr-6 sticky right-0 z-10 bg-white group-hover:bg-slate-50 transition-colors shadow-[-5px_0_10px_rgba(0,0,0,0.02)]">
                                        <div className="flex justify-end gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
                                            <button 
                                                onClick={() => { setEditingGestion(g); setShowModal(true); }}
                                                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100 transition-all"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg>
                                            </button>
                                            <button 
                                                onClick={() => setDeletionConfirm({ show: true, itemToDelete: g })}
                                                className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100 transition-all"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

            {/* MODALES */}
            <ModalPostVenta 
                show={showModal} 
                onClose={() => setShowModal(false)} 
                poliza={{
                    ...polizaData,
                    path: `public/data/clientes/${clientId}/polizas/${polizaId}` 
                }}
                initialData={editingGestion}
                onSuccess={() => {
                    setShowModal(false);
                    setNotification({ show: true, message: 'La etapa de postventa se ha actualizado correctamente.', type: 'success' });
                }}
            />

            <ModalNotificacion 
                show={notification.show} 
                onClose={() => setNotification({ ...notification, show: false })} 
                message={notification.message} 
                type={notification.type} 
            />

            <ModalConfirmacion 
                show={deletionConfirm.show} 
                onClose={() => setDeletionConfirm({ show: false, itemToDelete: null })} 
                onConfirm={handleDeleteStage} 
                title="Eliminar Etapa" 
                message="¿Estás seguro de que deseas eliminar esta etapa del historial? Esta acción no se puede deshacer." 
            />

            <ModalConfirmacion 
                show={deleteAllConfirm} 
                onClose={() => setDeleteAllConfirm(false)} 
                onConfirm={handleDeleteFull} 
                title="Eliminar Todo el Post Venta" 
                message="¿ESTÁS SEGURO? Esta acción eliminará el Número de Gestión, todo el historial de etapas y la póliza saldrá definitivamente del Tablero de Post Venta." 
            />
        </div>
    );
};

export default PostVentaTab;